var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from '@emotion/styled';
import { Button, Flex, Tooltip } from '@robinpowered/ui-kit';
import { useTranslation } from 'react-i18next';
import { useCalendarIntegrationsContext } from '../context/CalendarIntegrationContext';
import { useMemo, useState } from 'react';
import moment from 'moment';
import { useConfig } from '../context/ConfigContext';
import queryString from 'query-string';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { useSafeFormData } from '../types/index';
import { useEventDateTime } from '../hooks/useDateTime';
import { useFormContext } from 'react-hook-form';
import { useEventComposerGlobalContext } from '../composers/EventComposerGlobalContext';
import { useCreateEventContext } from '../composers/CreateEventContext';
import { hasAnySpaceConflicts, isCreateButtonDisabled, rruleEndsBeforeStart, } from '../utils/validationUtils';
import { useAdvancedBookingThreshold } from '../hooks/useAdvancedBookingThreshold';
export var CreateFooterButtons = function () {
    var t = useTranslation('meetingRooms').t;
    var environmentUrls = useConfig().environmentUrls;
    var dashboardUrl = environmentUrls.dashboardUrl;
    var formState = useFormContext().formState;
    var formData = useSafeFormData();
    var startTime = formData.startTime, endTime = formData.endTime, invitees = formData.invitees, title = formData.title, isPrivateEvent = formData.isPrivateEvent, uid = formData.uid, isAllDayEvent = formData.isAllDayEvent, attendeeCount = formData.attendeeCount, spacesWithConflicts = formData.spacesWithConflicts;
    var _a = useEventComposerGlobalContext(), requestOnlySpace = _a.requestOnlySpace, timezone = _a.timezone, currentOrg = _a.currentOrg, currentUser = _a.currentUser, requestFormId = _a.requestFormId;
    var _b = useCreateEventContext(), handleEventCreation = _b.handleEventCreation, creatingEvent = _b.creatingEvent, createEventError = _b.createEventError, completedSpaceRequestFormId = _b.completedSpaceRequestFormId;
    var isSpaceRequestOnlyEvent = !!requestOnlySpace;
    var isStartAndEndTimeValid = useEventDateTime({ timezone: timezone }).isStartAndEndTimeValid;
    var _c = useCalendarIntegrationsContext(), isLoadingCalendarIntegration = _c.isLoadingCalendarIntegration, canCreateRequestWithPersonalCalendar = _c.canCreateRequestWithPersonalCalendar;
    var checkFlag = useFeatureFlags().checkFlag;
    var allowSpaceRequestFormFeatureFlag = checkFlag('hydra(space-request-forms)', false);
    var isSpaceRequestFormCompleted = !!completedSpaceRequestFormId;
    /* @TODO this should be in the global context */
    var hasUnansweredSpaceRequestForm = allowSpaceRequestFormFeatureFlag &&
        isSpaceRequestOnlyEvent &&
        !!requestFormId &&
        isSpaceRequestFormCompleted === false;
    var buttonLabel = isSpaceRequestOnlyEvent
        ? t('create_event.request')
        : t('create_event.save');
    var _d = __read(useState(false), 2), isRedirectingToOldComposer = _d[0], setIsRedirectingToOldComposer = _d[1];
    var someSpaceConflicts = useMemo(function () { return hasAnySpaceConflicts(spacesWithConflicts); }, [spacesWithConflicts]);
    var recurrenceEndsBeforeStart = rruleEndsBeforeStart(formData.recurrenceRule, formData.date);
    var conflictingSpaceIds = useAdvancedBookingThreshold().conflictingSpaceIds;
    var hasAbtConflict = conflictingSpaceIds.length > 0;
    var isEventValid = !someSpaceConflicts &&
        isStartAndEndTimeValid &&
        !recurrenceEndsBeforeStart &&
        !hasAbtConflict;
    var redirectToOldComposer = function () {
        var _a, _b;
        setIsRedirectingToOldComposer(true);
        if (!invitees) {
            return;
        }
        var queryParams = {
            title: title,
            is_private: isPrivateEvent,
            org: currentOrg === null || currentOrg === void 0 ? void 0 : currentOrg.id,
            started_at: startTime === null || startTime === void 0 ? void 0 : startTime.toJSON(),
            ended_at: endTime === null || endTime === void 0 ? void 0 : endTime.toJSON(),
            space_id: (_b = (_a = formData.spaces) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id,
            redirectTo: window.location.href,
            uid: uid,
            timezone: timezone || moment.tz.guess(),
            isAllDay: !!isAllDayEvent,
        };
        var hasMultipleInvitees = invitees.length > 1;
        var hasEstimatedInvitees = attendeeCount && isSpaceRequestOnlyEvent;
        var inviteesParam = hasMultipleInvitees || hasEstimatedInvitees
            ? isSpaceRequestOnlyEvent
                ? "&inviteeCount=".concat(attendeeCount)
                : "&".concat(invitees
                    .filter(function (invitee) { return invitee.user_id !== (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id); })
                    .map(function (invitee) { return "invitees=".concat(invitee.email); })
                    .join('&'))
            : '';
        window.location.href = "".concat(dashboardUrl, "/event-composer?").concat(queryString.stringify(queryParams)).concat(inviteesParam);
    };
    var formHasErrors = Object.keys(formState.errors).length > 0;
    return (_jsxs(BottomRowButtons, { children: [_jsx(Tooltip, { title: !canCreateRequestWithPersonalCalendar
                    ? t('create_event.connect_work_calendar')
                    : hasUnansweredSpaceRequestForm ||
                        (isSpaceRequestOnlyEvent && !title)
                        ? t('create_event.answer_space_request_form')
                        : formHasErrors
                            ? t('create_event.form_has_errors')
                            : recurrenceEndsBeforeStart
                                ? t('eventDetails.recurrence.ends_before_start')
                                : hasAbtConflict
                                    ? t('eventDetails.recurrence.advanced_booking_threshold_conflict')
                                    : !isEventValid
                                        ? t('create_event.event_is_invalid')
                                        : undefined, children: _jsx(Button, { variant: "dashed", type: "primary", size: "large", style: { width: '100%', flex: 1 }, "data-survey-trigger": "create-event", onClick: function () { return handleEventCreation(); }, loading: (creatingEvent && !createEventError) || isLoadingCalendarIntegration, disabled: isCreateButtonDisabled({
                        isEventValid: isEventValid,
                        isRedirectingToOldComposer: isRedirectingToOldComposer,
                        canCreateRequestWithPersonalCalendar: canCreateRequestWithPersonalCalendar,
                        isLoadingCalendarIntegration: isLoadingCalendarIntegration,
                        hasUnansweredSpaceRequestForm: hasUnansweredSpaceRequestForm,
                        formHasErrors: formHasErrors,
                        isSpaceRequestOnlyEvent: isSpaceRequestOnlyEvent,
                        title: title,
                    }), children: buttonLabel }) }), _jsx(Button, { variant: "dashed", size: "large", style: { width: '100%', flex: 1 }, onClick: function () {
                    redirectToOldComposer();
                }, loading: isRedirectingToOldComposer && !createEventError, disabled: creatingEvent || !isEventValid, children: t('create_event.more_options') })] }));
};
var BottomRowButtons = styled(Flex)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: row;\n  gap: 8px;\n"], ["\n  display: flex;\n  flex-direction: row;\n  gap: 8px;\n"])));
var templateObject_1;
